import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { cn } from '@lib/utils';
import { getOverlayContainer } from '@lib/overlay/container';

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} {...props} />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = (props: React.ComponentProps<typeof DrawerPrimitive.Portal>) => (
  <DrawerPrimitive.Portal container={getOverlayContainer()} {...props} />
);

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    data-overlay-scrim=""
    // Radix wraps RemoveScroll around the OVERLAY (react-dialog/dist/index.mjs:116) and
    // keeps it mounted until its exit animation ends, and vaul's injected CSS gives that
    // a 500ms fadeOut (vaul/dist/index.mjs:62) — so the page stayed scroll-locked for half
    // a second after every close. The short exit keeps that window imperceptible. The
    // sheet below is shortened too: its DismissableLayer stays armed until IT unmounts,
    // so a tap inside vaul's 500ms window dispatched a stale dismiss — reopening a
    // drawer closed it again at once. See docs/overlays/overlay-system-design.md §1.
    className={cn(
      'fixed inset-0 z-overlay bg-black/80 data-[state=closed]:[animation-duration:120ms]!',
      className,
    )}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

/**
 * The backdrop of a sheet stacked over the drawer that opened it (a confirm over its
 * parent). Same element and geometry as DrawerOverlay, two deliberate differences:
 *
 * - `data-overlay-dimmer`, NOT `data-overlay-scrim`. A scrim claims the screen and there
 *   is exactly one on screen; a dimmer only separates a parent from the sheet stacked on
 *   it. Keeping the attributes distinct keeps the one-scrim invariant true while the pair
 *   still reads as stacked.
 * - Lighter. Two 80% scrims composite to 96% black, which is why the child's overlay was
 *   dropped in the first place; 40% over the parent's 80% lands at ~88% on the page while
 *   dimming the parent surface itself by a visible 40%.
 *
 * Rendering it at all (rather than nothing) is also what gives a nested sheet Radix's
 * RemoveScroll and its dismissable-surface registration — both live on the overlay.
 */
const DrawerDimmer = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    data-overlay-dimmer=""
    className={cn(
      'fixed inset-0 z-overlay bg-black/40 data-[state=closed]:[animation-duration:120ms]!',
      className,
    )}
    {...props}
  />
));
DrawerDimmer.displayName = 'DrawerDimmer';

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    hideHandle?: boolean;
    /**
     * This content stacks over the drawer that opened it — the parent's scrim is
     * already painting, so this one omits its own (one scrim on screen). Opt-in at
     * the call site: the same component is also used
     * standalone, where it must keep its scrim.
     *
     * It does NOT mean "no backdrop": it swaps the scrim for a lighter DrawerDimmer
     * (above). That matters twice over. Visually, a drawer's own scrim paints BELOW
     * its own content, so a parent can never dim itself — without a layer above it the
     * pair reads as one flat surface, which is how this was caught on device.
     * Mechanically, Radix mounts RemoveScroll and the dismissable-surface registration
     * on the OVERLAY, so omitting it outright left a nested sheet holding no scroll
     * lock of its own.
     *
     * One caveat survives: the dimmer passes `shards: [contentRef]` for its own root
     * only, so a nested sheet is not in the PARENT's shard list. Keep nested content
     * short — a confirm, two lines and a pair of buttons. Anything that needs to scroll
     * independently wants to replace its parent, not stack on it.
     */
    nested?: boolean;
    /**
     * A ref to this content's own overlay (the scrim, or the dimmer when `nested`). Radix portals
     * the overlay and the content separately into one shared container, so an outside
     * pointer-down that must mean "a tap on MY scrim" compares against this element.
     */
    overlayRef?: React.Ref<HTMLDivElement>;
    /** Classes for this content's own overlay (the scrim, or the dimmer when `nested`). */
    overlayClassName?: string;
  }
>(
  (
    {
      className,
      children,
      hideHandle,
      nested,
      overlayRef,
      overlayClassName,
      onPointerDownOutside,
      ...props
    },
    ref,
  ) => (
    <DrawerPortal>
      {nested ? (
        <DrawerDimmer ref={overlayRef} className={overlayClassName} />
      ) : (
        <DrawerOverlay ref={overlayRef} className={overlayClassName} />
      )}
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-x-0 bottom-0 z-overlay mt-24 flex h-auto flex-col rounded-t-2xl border bg-background [--mb-surface:var(--color-background)] app-fixed',
          // Exit shortened with the scrim (see DrawerOverlay): vaul's 500ms slideToBottom
          // kept this layer mounted and armed, so the first tap after a close hit a stale
          // dismissable layer instead of the page.
          'data-[state=closed]:[animation-duration:120ms]!',
          className,
        )}
        {...props}
        onPointerDownOutside={e => {
          // A tap or swipe on a toast is not a tap outside the sheet: it never dismisses a
          // drawer. Visible toasts take pointer events (ui/sonner.tsx), so the pointer-down lands
          // on the toast, not on the scrim; vaul then skips Radix's dismiss for a prevented event.
          // The caller's handler still runs and sees `defaultPrevented`.
          const target = e.detail.originalEvent.target;
          if (target instanceof Element && target.closest('[data-sonner-toaster]')) {
            e.preventDefault();
          }
          onPointerDownOutside?.(e);
        }}
      >
        {!hideHandle && (
          <div className="mx-auto mt-4 h-2 w-[100px] shrink-0 rounded-full bg-muted" />
        )}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  ),
);
DrawerContent.displayName = 'DrawerContent';

// The div's own props, `ref` among them (a prop in React 19), for a header that measures itself.
const DrawerHeader = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-auto flex flex-col gap-2 p-4', className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

/**
 * Keyboard sheet recipe, for a drawer with a text input. Targets iOS Safari
 * flinging the sheet off the top when the keyboard opens; applies on every
 * platform. Two parts:
 *   1. This height cap on DrawerContent. vaul's keyboard handler (repositionInputs,
 *      on by default) lifts the sheet by the keyboard height; the cap keeps a tall
 *      sheet's top on screen.
 *   2. useFixedBodyLock(open) (@hooks/useFixedBodyLock) in the drawer.
 * A 16px input font alone does not fix fly-to-top. The cap alone, without the
 * lock, also keeps the top of a sheet with no input on screen when its text
 * outgrows it (ConfirmDeleteDrawer). Under the cap, the house drawers put
 * everything below the handle in CAPPED_SHEET_BODY_CLASS (./cappedSheet), which
 * scrolls their buttons into reach where even they don't fit: ConfirmDeleteDrawer
 * always, ConfirmActionDrawer while it holds content. Device record and the
 * other lock combinations: docs/overlays/overlay-system-design.md §5.
 */
const KEYBOARD_SHEET_MAX_HEIGHT = 'max-h-[calc(100dvh-4rem)]';

export {
  KEYBOARD_SHEET_MAX_HEIGHT,
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
